import { useState, useEffect } from 'react';
import { useAdmin } from '@/contexts/AdminContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Edit, Trash2, Save, X, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminTestimonialsPage = () => {
  const { getAuthHeader } = useAdmin();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [formData, setFormData] = useState({ name: '', role: '', content: '', avatar: '', order: 0 });

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/testimonials`);
      setItems(response.data);
    } catch (error) {
      toast.error('Failed to load testimonials');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setFormData({ name: item.name, role: item.role, content: item.content, avatar: item.avatar, order: item.order });
  };

  const handleAdd = () => {
    setIsAdding(true);
    setFormData({ name: '', role: '', content: '', avatar: '', order: items.length });
  };

  const handleCancel = () => {
    setEditingId(null); setIsAdding(false);
    setFormData({ name: '', role: '', content: '', avatar: '', order: 0 });
  };

  const handleSave = async () => {
    try {
      if (isAdding) {
        await axios.post(`${API}/testimonials`, formData, { headers: getAuthHeader() });
        toast.success('Testimonial added');
      } else {
        await axios.put(`${API}/testimonials/${editingId}`, formData, { headers: getAuthHeader() });
        toast.success('Testimonial updated');
      }
      fetchItems(); handleCancel();
    } catch (error) {
      toast.error('Failed to save');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this testimonial?')) return;
    try {
      await axios.delete(`${API}/testimonials/${id}`, { headers: getAuthHeader() });
      toast.success('Deleted');
      fetchItems();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/admin/dashboard"><Button variant="outline" size="icon" className="rounded-full"><ArrowLeft size={18} /></Button></Link>
              <div><h1 className="font-heading text-3xl font-bold">Manage Testimonials</h1></div>
            </div>
            {!isAdding && !editingId && <Button onClick={handleAdd} className="bg-[#10B981] rounded-full"><Plus size={18} className="mr-2" />Add Testimonial</Button>}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {(isAdding || editingId) && (
          <div className="bg-white rounded-2xl p-8 border mb-8">
            <h2 className="font-heading text-2xl font-bold mb-6">{isAdding ? 'Add' : 'Edit'} Testimonial</h2>
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div><Label>Name</Label><Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} /></div>
                <div><Label>Role</Label><Input value={formData.role} onChange={(e) => setFormData({...formData, role: e.target.value})} placeholder="CEO, Company" /></div>
              </div>
              <div><Label>Content</Label><Textarea value={formData.content} onChange={(e) => setFormData({...formData, content: e.target.value})} rows={4} /></div>
              <div><Label>Avatar URL</Label><Input value={formData.avatar} onChange={(e) => setFormData({...formData, avatar: e.target.value})} placeholder="https://ui-avatars.com/api/?name=Name" /></div>
              <div className="flex gap-4">
                <Button onClick={handleSave} className="bg-[#10B981] rounded-full"><Save size={18} className="mr-2" />Save</Button>
                <Button onClick={handleCancel} variant="outline" className="rounded-full"><X size={18} className="mr-2" />Cancel</Button>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-4">
          {items.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 text-center border">
              <p className="text-slate-600 mb-4">No testimonials yet</p>
              <Button onClick={handleAdd} className="bg-[#10B981] rounded-full"><Plus size={18} className="mr-2" />Add Testimonial</Button>
            </div>
          ) : (
            items.map((item) => (
              <div key={item.id} className="bg-white rounded-2xl p-6 border hover:border-[#10B981]">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-heading text-xl font-bold">{item.name}</h3>
                    <p className="text-sm text-slate-600 mb-2">{item.role}</p>
                    <p className="text-slate-700">{item.content}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => handleEdit(item)} variant="outline" size="icon" className="rounded-full"><Edit size={18} /></Button>
                    <Button onClick={() => handleDelete(item.id)} variant="outline" size="icon" className="rounded-full text-red-600"><Trash2 size={18} /></Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
